import React, { useEffect, useMemo, useState } from "react";
import SearchInputV2 from "./SearchInputV2";
import { Pagination } from "./Pagination";

function getCellSearchValue(item, column) {
    if (typeof column.searchValue === "function") {
        return column.searchValue(item);
    }

    if (typeof column.key === "string") {
        return item?.[column.key];
    }

    return "";
}

export function ModalListTable({
    items = [],
    columns = [],
    loading = false,
    emptyMessage = "No records found.",
    noResultsMessage = "No matching records found.",
    searchPlaceholder = "Search...",
    pageSize = 5,
    getSearchValue,
    getRowKey = (item, index) => item?.id ?? index,
    onRowClick,
    getRowClassName,
    variant = "default",
    hideHeaderWhenEmpty = false,
    hidePaginationWhenEmpty = false
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);

    const filteredItems = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) {
            return items;
        }

        return items.filter((item) => {
            const rawValue = typeof getSearchValue === "function"
                ? getSearchValue(item)
                : columns.map((column) => getCellSearchValue(item, column)).join(" ");

            return String(rawValue || "").toLowerCase().includes(normalizedSearch);
        });
    }, [columns, getSearchValue, items, searchTerm]);

    const totalPages = Math.ceil(filteredItems.length / pageSize);
    const maxPage = totalPages > 0 ? totalPages - 1 : 0;
    const safeCurrentPage = Math.min(currentPage, maxPage);
    const pageStart = safeCurrentPage * pageSize;
    const pageItems = filteredItems.slice(pageStart, pageStart + pageSize);

    useEffect(() => {
        setCurrentPage(0);
    }, [items.length, searchTerm]);

    useEffect(() => {
        if (safeCurrentPage !== currentPage) {
            setCurrentPage(safeCurrentPage);
        }
    }, [currentPage, safeCurrentPage]);

    const emptyStateMessage = searchTerm.trim() ? noResultsMessage : emptyMessage;
    const isEmptyState = !loading && pageItems.length === 0;
    const shouldRenderHeader = !(hideHeaderWhenEmpty && isEmptyState);
    const shouldRenderPagination = !(hidePaginationWhenEmpty && filteredItems.length === 0);
    const rootClassName = [
        "oct_modal-list",
        variant === "schedule" ? "oct_modal-list--schedule" : ""
    ].filter(Boolean).join(" ");

    return (
        <div className={rootClassName}>
            <div className="oct_modal-list__search">
                <SearchInputV2
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={searchPlaceholder}
                    className="oct_modal-list__search-input"
                />
            </div>

            <div className="oct_modal-list__table-shell">
                <div className="oct_modal-list__table-wrap">
                    <table className="oct_modal-list__table">
                        {shouldRenderHeader ? (
                            <thead>
                                <tr>
                                    {columns.map((column, index) => (
                                        <th
                                            key={column.key || column.label || index}
                                            className={column.headerClassName || ""}
                                        >
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        ) : null}
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="oct_modal-list__empty">
                                        Loading...
                                    </td>
                                </tr>
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="oct_modal-list__empty">
                                        {emptyStateMessage}
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((item, index) => {
                                    const rowClassName = [
                                        "oct_modal-list__row",
                                        onRowClick ? "oct_modal-list__row--interactive" : "",
                                        typeof getRowClassName === "function" ? getRowClassName(item) : ""
                                    ].filter(Boolean).join(" ");

                                    return (
                                        <tr
                                            key={getRowKey(item, pageStart + index)}
                                            className={rowClassName}
                                            onClick={onRowClick ? () => onRowClick(item) : undefined}
                                        >
                                            {columns.map((column, columnIndex) => (
                                                <td
                                                    key={column.key || column.label || columnIndex}
                                                    className={column.className || ""}
                                                >
                                                    {typeof column.render === "function"
                                                        ? column.render(item, pageStart + index)
                                                        : (column.key ? item?.[column.key] : "") || "-"}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {shouldRenderPagination ? (
                    <Pagination
                        total={totalPages}
                        current={safeCurrentPage}
                        totalRecords={filteredItems.length}
                        onClick={(value) => setCurrentPage(value)}
                    />
                ) : null}
            </div>
        </div>
    );
}

export default ModalListTable;
